import { ArrowPathIcon, PlayIcon } from "@heroicons/react/24/solid";
import { ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import type { TrainingResumePrompt } from "@/lib/hooks/training-simulator/use-training-resume-prompt.ts";

type TrainingResumeModalProps = TrainingResumePrompt;

export function TrainingResumeModal({
  open,
  levelPosition,
  currentMessage,
  totalMessages,
  isStartingOver,
  onResume,
  onStartOver,
}: TrainingResumeModalProps) {
  return (
    <Dialog open={open}>
      <DialogContent
        className="gap-0 overflow-hidden rounded-md p-0 sm:max-w-3xl"
        hideCloseButton
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="relative flex flex-col justify-center gap-3 overflow-hidden border-b border-surfaces-divider bg-surfaces-z3 p-6">
          <img
            src="/assets/training-simulator/welcome-texture.png"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-1 top-0 h-full max-w-none select-none"
          />
          <div className="relative z-10 flex flex-col gap-3">
            <h2 className="text-header-2 text-text-primary">Welcome back.</h2>
            <p className="max-w-md text-body-1 text-text-secondary">
              Looks like you stepped away partway through. You were on message{" "}
              {currentMessage} of {totalMessages}. Pick up where you left off,
              or start the level fresh.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center gap-6 border-b border-surfaces-divider p-6">
          <div className="flex w-full flex-col gap-3">
            <ResumeOption
              icon={<PlayIcon className="size-5 text-brand-trueWhite" />}
              iconClassName="bg-brand-forest"
              title="Resume where you left off"
              description={`Keep your conversation so far and continue from message ${currentMessage}.`}
              disabled={isStartingOver}
              onClick={onResume}
            />
            <ResumeOption
              icon={<ArrowPathIcon className="size-5 text-text-primary" />}
              iconClassName="bg-black/10"
              title="Start the level over"
              description={`Clear this conversation and begin Level ${levelPosition} from the first message.`}
              loading={isStartingOver}
              disabled={isStartingOver}
              onClick={onStartOver}
            />
          </div>
          <p className="text-center text-body-1 text-text-secondary opacity-50">
            Either way, you&apos;ll head straight into the level.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ResumeOptionProps {
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
  description: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function ResumeOption({
  icon,
  iconClassName,
  title,
  description,
  loading,
  disabled,
  onClick,
}: ResumeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-md border border-black/10 py-3 pl-[18px] pr-3 text-left transition-colors hover:bg-surfaces-z3 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-md ${iconClassName}`}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-body-1 font-bold text-text-primary">{title}</span>
        <span className="text-body-1 text-text-secondary opacity-75">
          {description}
        </span>
      </span>
      {loading ? (
        <Loader2 className="size-5 shrink-0 animate-spin text-text-secondary" />
      ) : (
        <ChevronRight className="size-5 shrink-0 text-text-secondary" />
      )}
    </button>
  );
}
